import React from 'react'

import IFace from '../ViewerIFace'

import { getEmailName, getUserEmail, getUserDomain, isEmail, uiInfo } from './utils'
import type { FileIconState } from './utils'
import { getDisplayEmailElem, getEmailOptionElem } from './uiHelpers'
import type { EmailOptions } from './uiHelpers'

export { getEmailName, setUserEmail, getUserEmail, getUserDomain, isEmail } from './utils'
export type { FileIconState } from './utils'
export { getDisplayEmailElem, getEmailOptionElem } from './uiHelpers'
export type { EmailOptions } from './uiHelpers'

interface FileIconResult {
  url?: string
  type?: string
  data?: string
  showExt?: boolean
}

export function processEmailEvent(e?: React.MouseEvent): void {
  if (e != null) {
    const emailElem = (e.target as HTMLElement).closest('.emailSet[data-email]')
    if (emailElem != null) {
      const email = emailElem.getAttribute('data-email')
      const name = emailElem.getAttribute('data-name')
      IFace.WebToNative.showContact(email, name)

      e.preventDefault()
      e.stopPropagation()
      return
    }
  }
}

/**
 * HTML 메타문자 escape — innerHTML 보간 안전화.
 *
 * `getDisplayEmailHTMLStr` 는 결과를 HTML 문자열로 반환하고, 소비처가 innerHTML /
 * html-react-parser(`parse`) 로 파싱한다(EmailView `buildPrintDocument`·`printPayload`,
 * uiExt `getICalElem`). 따라서 name/email 을 이 sink 에서 escape 해야 표시명에 든 마크업이
 * 실행되지 않는다. JSX 경로(`getDisplayEmailElem`)는 React 가 자동 escape 하므로 raw 를
 * 그대로 받고, 데이터(adapter)는 raw 를 공급한다 — escape 는 데이터가 아니라 sink 소유.
 */
function htmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function getDisplayEmailHTMLStr(options: EmailOptions): string {
  // raw 입력을 진입부에서 1회 escape 한다. 이후 로직/문자열 생성은 예전(=escaped 입력을
  // 받던 시절)과 동일한 값으로 돌아가므로 출력 HTML 은 바이트 동일하게 유지된다.
  const name = options.name != null ? htmlEscape(options.name) : options.name
  const email = options.email != null ? htmlEscape(options.email) : options.email
  if (isEmail(email) === true) {
    const domain = getUserDomain()

    let text = ''
    if (email!.endsWith(domain!) === true) {
      text = name || email!
    } else if (name == null || name === email) {
      text = email!
    } else {
      text = `${name} &lt;${email}&gt;`
    }

    let propStr = `data-email="${email}"`
    if (name != null && name.length > 0) {
      propStr = `${propStr} data-name="${name}"`
    }

    return `<span class="emailSet" ${propStr}>${text}</span>`
  } else if (name != null && (email == null || email.length === 0)) {
    return name
  }

  return email || ''
}

export async function getFileIcon(fName?: string): Promise<FileIconState> {
  const fileName = fName || ''
  if (uiInfo.fileIconMap == null) {
    uiInfo.fileIconMap = {}
  }

  if (uiInfo.fileIconMap[fileName] == null) {
    const pos = fileName.lastIndexOf('.')
    const ext = fileName.substring(pos + 1)

    const ret = (await IFace.WebToNative.getFileIcon(fileName)) as FileIconResult
    const { url, type, data, showExt } = ret
    const newState: FileIconState = {}
    if (url != null) {
      newState.imgSrc = url
      newState.alt = ext
    } else if (data != null && type != null) {
      const dataUrl = `data:${type};base64,${data}`
      newState.imgSrc = dataUrl
      newState.alt = ext
    }

    if (showExt === true || newState.imgSrc == null) {
      newState.text = ext
    }

    uiInfo.fileIconMap[fileName] = newState
  }

  return uiInfo.fileIconMap[fileName]
}

const helper = {
  getEmailName,
  getUserEmail,
  getUserDomain,
  processEmailEvent,
  getDisplayEmailElem,
  getFileIcon,
  getEmailOptionElem,
  isEmail,
}

export default helper
